import { FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Component, DestroyRef, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AsyncPipe, NgClass, DatePipe } from '@angular/common';
import { BehaviorSubject, combineLatest, exhaustMap, of, Subject, tap } from "rxjs";
import { takeUntil } from 'rxjs';
import { filter, map, startWith, switchMap } from "rxjs/operators";
import { FolderService } from "../../services/folder.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatButton } from '@angular/material/button';
import { FolderTreeComponent } from '../../components/folder-tree/folder-tree.component';
import { MatOption, provideNativeDateAdapter } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel, MatError, MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatCard, MatCardHeader, MatCardTitle, MatCardSubtitle, MatCardContent } from '@angular/material/card';
import { EditPersonCommandRequest, SharepointSiteDto, SharepointFolderDto, PersonsApiService, SharePointApiService, GetSharePointFoldersFromSiteCommandResponse } from '../../../../../api/generated';
import { ModelToFormGroup, loadingObservable, notNullOrUndefined, hasRequiredProperties } from '../../../../../shared/utils';
import { ConfirmComponent, ConfirmResult } from '../../components/confirm/confirm.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DateUtils } from './date-utils';

type Model = Omit<EditPersonCommandRequest, 'modified'>;

@Component({
  selector: 'app-person-edit',
  templateUrl: './person-edit.component.html',
  styleUrls: ['./person-edit.component.scss'],
  standalone: true,
  providers: [provideNativeDateAdapter(), DatePipe],
  imports: [FormsModule, MatCard, MatCardHeader, MatCardTitle, MatCardSubtitle, MatCardContent, MatProgressSpinner, MatFormField, MatFormFieldModule, MatLabel, MatInput, ReactiveFormsModule, MatError, MatSelect, MatOption, FolderTreeComponent, MatButton, MatDatepickerModule, RouterLink, AsyncPipe, TranslateModule, NgClass, DatePipe]
})
export class PersonEditComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private destroy$ = new Subject<void>();
  private readonly pageSize = 25;
  private streamParentFolder: SharepointFolderDto | null = null;
  private streamPage = 1;
  private streamedFolders: SharepointFolderDto[] = [];

  readonly minDate: Date = new Date();
  readonly maxDate: Date = new Date(Date.now() + 180 * 24 * 60 * 60 * 1000);
  sites: SharepointSiteDto[] = [];
  savedSiteId: string | null = null;
  folders$ = new BehaviorSubject<SharepointFolderDto[]>([] as SharepointFolderDto[]);
  selectedFolder: SharepointFolderDto | null = null;
  disableTokenButton: boolean = false;
  statusDates = {
    createdAt: null as Date | null,
    sentAt: null as Date | null,
    loggedIn: null as Date | null,
    downloadedAt: null as Date | null,
    uploadedAt: null as Date | null
  };

  get statusDatesLocal() {
    return {
      createdAt: this.statusDates.createdAt,
      sentAt: this.statusDates.sentAt,
      loggedIn: this.statusDates.loggedIn,
      downloadedAt: this.statusDates.downloadedAt,
      uploadedAt: this.statusDates.uploadedAt
    };
  }

  PersonRightsTypes: Array<{ value: Exclude<EditPersonCommandRequest['rights'], undefined>, text: string }> = [
    { value: 'Download', text: 'download' },
    { value: 'Upload', text: 'upload' },
  ];

  get preventSubmit() {
    return this.loading || this.form.invalid;
  }

  form = new FormGroup<ModelToFormGroup<Model>>({
    guid: new FormControl('', { validators: [Validators.required], nonNullable: true }),
    firstName: new FormControl(null, [Validators.required]),
    lastName: new FormControl(null, [Validators.required]),
    email: new FormControl(null, [Validators.required, Validators.email]),
    rights: new FormControl('Unassigned', { nonNullable: true }),

    siteId: new FormControl(null, [Validators.required]),
    driveId: new FormControl(null, [Validators.required]),
    folderId: new FormControl(null, [Validators.required]),
    folderName: new FormControl(null, [Validators.required]),
    folderUrl: new FormControl(null, [Validators.required]),
    language: new FormControl(null, [Validators.required]),
    endDate: new FormControl({ value: null, disabled: true }),
    endDateType: new FormControl('7'),
    message: new FormControl(null)
  });

  ngOnInit(): void {
    this.translate.get(['download', 'upload']).subscribe(translations => {
      this.PersonRightsTypes = [
        { value: 'Download', text: translations['download'] },
        { value: 'Upload', text: translations['upload'] }
      ];
    });

    // Set initial value for endDate
    this.updateEndDate(this.form.controls.endDateType.value ?? null, this.form.controls.endDate.value);

    // Listen for changes to endDateType
    this.form.controls.endDateType.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(type => {
        this.updateEndDate(type ?? null, this.form.controls.endDate.value);
      });

    // Listen for changes to endDate only when custom is selected
    this.form.controls.endDate.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(val => {
        if (this.form.controls.endDateType.value === 'custom' && val) {
          const dateObj = DateUtils.parseDate(val);
          if (dateObj) {
            const dateTimeString = DateUtils.getDateTimeString(dateObj);
            if (val !== dateTimeString) {
              this.form.controls.endDate.setValue(dateTimeString, { emitEvent: false });
            }
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Updates the endDate control based on the type and value.
   * DRY: Handles both preset and custom logic in one place.
   */
  private updateEndDate(type: string | null, value: unknown) {
    this.form.controls.endDate.clearValidators();
    this.form.controls.endDate.updateValueAndValidity();

    if (type === 'custom') {
      this.form.controls.endDate.enable();
      this.form.controls.endDate.setValidators([Validators.required]);
      this.form.controls.endDate.updateValueAndValidity();
      // If a value is present, format it
      if (value) {
        const dateObj = DateUtils.parseDate(value);
        if (dateObj) {
          const dateTimeString = DateUtils.getDateTimeString(dateObj);
          if (value !== dateTimeString) {
            this.form.controls.endDate.setValue(dateTimeString, { emitEvent: false });
          }
        }
      }
    } else {
      const days = parseInt(type ?? '', 10);
      if (!isNaN(days)) {
        const date = new Date();
        date.setDate(date.getDate() + days);
        const dateTimeString = DateUtils.getDateTimeString(date);
        this.form.controls.endDate.enable();
        this.form.controls.endDate.setValue(dateTimeString);
        this.form.controls.endDate.disable();
      }
    }
  }

  edit$ = new Subject<typeof this.form.value>();

  loading: boolean = false;
  loadingSite: boolean = false;
  loadingSites: boolean = false;
  loadingMoreFolders: boolean = false;
  hasMoreFolders: boolean = false;
  showError: boolean = false;

  constructor(
    private personService: PersonsApiService,
    private route: ActivatedRoute,
    private router: Router,
    private sharepointService: SharePointApiService,
    private folderService: FolderService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private translate: TranslateService
  ) {
    this.getSites();
    this.observeSiteChanges();
    this.observeFormSubmits();
    this.observeSelectedFolder();
    this.observeParamsChanges();
  }




  getSites() {
    loadingObservable(this.sharepointService.getApiV10SharePointGetSites())
      .subscribe({
        next: (response) => {
          if ('loading' in response) {
            this.loadingSites = response.loading;
          } else {
            this.sites = response.items;
            const site = this.form.controls.siteId.value;
            this.form.patchValue({
              siteId: null
            });
            this.form.patchValue({ siteId: site });
          }
        },
        error: (error) => {
          console.error('GET SHAREPOINT SITES FROM GRAPH API: ', error)
        }
      });
  }

  onFolderSelected(folder: SharepointFolderDto | null) {
    if (folder === null) {
      this.selectedFolder = null;
      this.form.patchValue({
        driveId: null,
        folderId: null,
        folderName: null,
        folderUrl: null
      });
      return;
    }

    this.form.patchValue({
      driveId: folder.sharePointDriveId,
      folderId: folder.id,
      folderName: folder.name,
      folderUrl: folder.webUrl
    });

    if (folder.hasChildren && (!folder.subFolders || folder.subFolders.length === 0)) {
      this.loadChildrenForFolder(folder, 1);
    }
  }

  onLoadMoreFolders() {
    this.loadNextStreamPage();
  }

  onCreateFolderRequested(event: { parentFolder: SharepointFolderDto, folderName: string }) {
    const folderName = event.folderName.trim();
    if (!folderName) {
      return;
    }

    this.loading = true;
    this.sharepointService.postApiV10SharePointCreateFolder({
      newFolderName: folderName,
      sharePointSiteId: event.parentFolder.sharePointSiteId,
      sharePointDriveId: event.parentFolder.sharePointDriveId,
      sharePointFolderId: event.parentFolder.id
    }).subscribe({
      next: (response) => {
        this.loading = false;
        this.loadChildrenForFolder(event.parentFolder, 1, response.newFolderId);
      },
      error: (error) => {
        this.loading = false;
        console.error('CREATE NEW FOLDER IN SHAREPOINT ERROR: ', error);
      }
    });
  }

  private loadChildrenForFolder(folder: SharepointFolderDto, page: number, selectFolderId?: string) {
    loadingObservable(
      this.sharepointService.postApiV10SharePointGetChildrenFolderStream({
        folderId: folder.id,
        driveId: folder.sharePointDriveId,
        siteId: folder.sharePointSiteId,
        page
      })
    ).subscribe({
      next: (response) => {
        if ('loading' in response) {
          this.loading = response.loading;
        } else {
          folder.subFolders = response.items;

          if (selectFolderId) {
            const selectedFolder = this.folderService.findFolderById(selectFolderId, this.folders$.value);
            if (selectedFolder) {
              this.selectedFolder = selectedFolder;
              this.onFolderSelected(selectedFolder);
            }
          }
        }
      },
      error: (error) => {
        console.error('GET SHAREPOINT CHILDREN REQUEST: ', error)
      }
    });
  }

  observeSiteChanges() {
    this.form.controls.siteId.valueChanges
      .pipe(
        filter(notNullOrUndefined),
        switchMap(siteId => {
          const site = this.sites.find(s => s.siteId === siteId);
          if (!site) {
            return of({ items: [] } as GetSharePointFoldersFromSiteCommandResponse);
          }

          if (this.savedSiteId == this.form.controls.siteId.value!) {
            const driveId = this.form.controls.driveId.value!;
            const folderId = this.form.controls.folderId.value!;
            return loadingObservable(this.sharepointService.postApiV10SharePointSearchFolder({ siteId: siteId, driveId: driveId, folderId: folderId }));
          } else {
            return loadingObservable(this.sharepointService.postApiV10SharePointGetFolders({ siteId: site.siteId, teamId: site.teamId, folderPath: this.form.get('folderUrl')?.value }));
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (response) => {
          if ('loading' in response) {
            this.loadingSite = response.loading;
          } else {
            this.initializeStreamFolders(response.items ?? []);
          }
        },
        error: (error) => {
          console.error('GET SHAREPOINT FOLDERS FROM GRAPH API: ', error);
        }
      });
  }

  observeFormSubmits() {
    this.edit$
      .pipe(
        filter(hasRequiredProperties(['guid'])),
        exhaustMap(form => loadingObservable(this.personService.putApiV10Persons(form.guid, form))),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({

        next: (response) => {
          if ('loading' in response) {
            this.loading = response.loading;
          } else {
            // this.router.navigate(['/', 'person-view']);
            this.translate.get(['modified', 'close']).subscribe(translations => {
              this.snackBar.open(translations['modified'], translations['close'], {
                duration: 5000,
                horizontalPosition: 'end',
                verticalPosition: 'top',
                panelClass: ['mat-success']
              });
            });
            this.sendToken();
          }
        },
        error: (error) => {
          this.showError = true;
          console.error('PUT PERSONS: ', error);
        }
      })
  }

  openSnackBar(message: string) {
    this.translate.get('close').subscribe((close: string) => {
      this.snackBar.open(message, close, { duration: 5000, horizontalPosition: 'end', verticalPosition: 'top' });
    });
  }

  observeSelectedFolder() {

    combineLatest([this.form.controls.folderId.valueChanges.pipe(startWith(this.form.controls.folderId.value)), this.folders$])
      .pipe(takeUntil(this.destroy$))
      .subscribe((values) => {
        const [folderId, folders] = values;
        if (notNullOrUndefined(folderId)) {
          const selectedFolder = this.folderService.findFolderById(folderId, folders);
          if (notNullOrUndefined(selectedFolder)) {
            this.selectedFolder = selectedFolder;
          }
        }
      });
  }

  observeParamsChanges() {
    this.route.params
      .pipe(
        tap(() => this.loading = true),
        map(params => params['id'] as string | undefined),
        filter(notNullOrUndefined),
        switchMap(id => this.personService.getApiV10Persons(id)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (response) => {
          this.loading = false;
          this.form.patchValue({
            guid: response.person.guid,
            firstName: response.person.firstName,
            lastName: response.person.lastName,
            email: response.person.email,
            folderUrl: response.person.sharePointFolderUrl,
            siteId: response.person.sharePointSite,
            folderId: response.person.sharePointFolderId,
            rights: response.person.rights,
            folderName: response.person.sharePointFolderName,
            driveId: response.person.sharePointDriveId,
            language: response.person.language,
            endDateType: 'custom',
            endDate: response.person.tokens[0]?.expireDateToken ?? null,
            message: response.person.tokens[0]?.message ?? null
          })
         
          this.statusDates = {
            createdAt: response.person.createdAt ? new Date(response.person.createdAt) : null,
            sentAt: response.person.sentAt ? new Date(response.person.sentAt) : null,
            loggedIn: response.person.loggedIn ? new Date(response.person.loggedIn) : null,
            downloadedAt: response.person.downloadedAt ? new Date(response.person.downloadedAt) : null,
            uploadedAt: response.person.uploadedAt ? new Date(response.person.uploadedAt) : null
          };
          this.savedSiteId = response.person.sharePointSite ?? null;
        },
        error: (error) => {
          this.loading = false;
          console.error(error);
        }
      });
  }

  editPerson() {
    if (this.form.valid) {
      this.translate.get(['emailPopup.title', 'emailPopup.content']).subscribe(translations => {
        const dialogRef = this.dialog.open(ConfirmComponent, {
          width: '600px',
          data: {
            title: translations['emailPopup.title'],
            content: translations['emailPopup.content'],
          },
        });

        dialogRef.afterClosed().subscribe(async (result: ConfirmResult) => {
          if (result.confirmed) {
            this.edit$.next(this.form.getRawValue());
          }
        });
      });
    }

  }

  private initializeStreamFolders(items: SharepointFolderDto[]) {
    this.streamParentFolder = items[0] ?? null;
    this.streamPage = 1;
    this.streamedFolders = [];
    this.selectedFolder = null;

    if (!this.streamParentFolder) {
      this.folders$.next([]);
      this.hasMoreFolders = false;
      return;
    }

    this.hasMoreFolders = true;
    this.folders$.next([]);
    this.loadNextStreamPage();
  }

  private loadNextStreamPage() {
    if (this.loadingMoreFolders || !this.hasMoreFolders || !this.streamParentFolder) {
      return;
    }

    this.loadingMoreFolders = true;
    this.sharepointService.postApiV10SharePointGetChildrenFolderStream({
      siteId: this.streamParentFolder.sharePointSiteId,
      driveId: this.streamParentFolder.sharePointDriveId,
      folderId: this.streamParentFolder.id,
      page: this.streamPage
    }).subscribe({
      next: (response) => {
        const pageItems = response?.items ?? [];
        this.streamedFolders = [...this.streamedFolders, ...pageItems];
        this.folders$.next([...this.streamedFolders]);
        this.streamPage += 1;
        this.hasMoreFolders = pageItems.length >= this.pageSize;
        this.loadingMoreFolders = false;
      },
      error: (error) => {
        this.loadingMoreFolders = false;
        this.hasMoreFolders = false;
        console.error('GET SHAREPOINT CHILDREN STREAM REQUEST: ', error);
      }
    });
  }


  sendToken() {
    this.disableTokenButton = true;
    this.translate.get('emailSend').subscribe((message: string) => {
      this.openSnackBar(message);
    });
    this.personService.postApiV10PersonsSendEmailWithToken({
      guid: this.form.controls.guid.value
    }).subscribe({
      next: (response) => {
        this.disableTokenButton = false;
      },
      error: (error) => {
        this.disableTokenButton = false;
        console.error(error);
      }
    });
  }
}